import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import config from '../config';
import { createAuth } from '../Helpers';

export const reducerName = 'personState';

export const getPerson = createAsyncThunk(
    'personState/getPerson',
    async (personId, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/person/${personId}`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const getAllPersons = createAsyncThunk(
    'personState/getAllPersons',
    async (_, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'GET',
                url: `${config.server}/api/person`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            const persons = response.data.reduce((obj, item) => {
                obj[item.id] = { ...item, showRadial: false };;
                return obj;
            }, {});
            return persons;
        } catch (error) {
            if (error.response?.status === 401 || error.response?.status === 404) {
                return rejectWithValue({ redirectTo: '/login', message: error.response?.data || 'An error occurred' });
            }
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const checkIn = createAsyncThunk(
    'personState/checkIn',
    async (personId, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/person/checkin/${personId}`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

export const checkOut = createAsyncThunk(
    'personState/checkOut',
    async (personId, { getState, rejectWithValue }) => {
        try {
            const response = await axios({
                method: 'POST',
                url: `${config.server}/api/person/checkout/${personId}`,
                withCredentials: true,
                auth: createAuth(getState),
            });
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'An error occurred');
        }
    }
);

const initialState = {
    persons: {},
    status: 'idle',
    error: null,
    redirectAfterError: null,
};

const personSlice = createSlice({
    name: reducerName,
    initialState,
    reducers: {
        showRadial: (state, action) => {
            const { personId, show } = action.payload;
            if (state.persons[personId]) {
                state.persons[personId].showRadial = show;
            }
        },
        clearRedirect: (state) => {
            state.redirectAfterError = null;
        },
    },
    extraReducers: (builder) => {
        // Handle getPerson
        builder
            .addCase(getPerson.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(getPerson.fulfilled, (state, action) => {
                const person = action.payload;
                state.persons[person.id] = person;
                state.status = 'succeeded';
            })
            .addCase(getPerson.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });

        // Handle getAllPersons
        builder
            .addCase(getAllPersons.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(getAllPersons.fulfilled, (state, action) => {
                state.persons = action.payload;
                state.status = 'succeeded';
            })
            .addCase(getAllPersons.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;

                if (action.payload?.redirectTo) {
                    state.redirectAfterError = action.payload.redirectTo;
                }
            });

        // Handle checkIn
        builder
            .addCase(checkIn.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(checkIn.fulfilled, (state, action) => {
                const updatedPerson = action.payload;
                state.persons[updatedPerson.id] = updatedPerson;
                state.status = 'succeeded';
            })
            .addCase(checkIn.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });

        // Handle checkOut
        builder
            .addCase(checkOut.pending, (state) => {
                state.status = 'loading';
                state.error = null;
            })
            .addCase(checkOut.fulfilled, (state, action) => {
                const updatedPerson = action.payload;
                state.persons[updatedPerson.id] = updatedPerson;
                state.status = 'succeeded';
            })
            .addCase(checkOut.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload;
            });
    },
});

export const { showRadial, clearRedirect } = personSlice.actions;

export default personSlice.reducer;